/**
 * TrainingQuizModal — post-completion knowledge check (Phase Q1).
 *
 * Optional and non-blocking: completion is already saved before this opens, and
 * skipping leaves it as "User-reported complete". Passing shows an honest message
 * that this improves confidence of UNDERSTANDING — never watch/Companion/provider
 * verification. Grading happens server-side (via the adapter); the client never
 * sees correct answers until the graded result comes back.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { QuizAdapter } from '@/lib/training/quiz/quizAdapter';
import type { QuizForTechnician, QuizAttemptResult, QuizAnswers } from '@/lib/training/quiz/quizTypes';
import { QUIZ_MAX_ATTEMPTS } from '@/lib/training/quiz/quizGrading';

export interface TrainingQuizModalProps {
  open: boolean;
  artifactId: string;
  sessionId: string | null;
  adapter: QuizAdapter;
  onClose: () => void;
  onResult?: (result: QuizAttemptResult) => void;
  /**
   * Optional pre-fetched quiz (from useTrainingQuizLauncher's approved-quiz gate).
   * When provided, the modal uses it directly and skips its own fetch. `undefined`
   * = not supplied (fetch as before); `null` = caller confirmed none exists.
   */
  prefetchedQuiz?: QuizForTechnician | null;
}

export function TrainingQuizModal({
  open,
  artifactId,
  sessionId,
  adapter,
  onClose,
  onResult,
  prefetchedQuiz,
}: TrainingQuizModalProps) {
  const [quiz, setQuiz] = useState<QuizForTechnician | null>(null);
  const [answers, setAnswers] = useState<QuizAnswers>([]);
  const [result, setResult] = useState<QuizAttemptResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!open) return;
    // Caller supplied the quiz (or explicit null) → use it, don't re-fetch.
    if (prefetchedQuiz !== undefined) {
      setQuiz(prefetchedQuiz);
      setAnswers(prefetchedQuiz ? new Array(prefetchedQuiz.questions.length).fill(-1) : []);
      return;
    }
    let cancelled = false;
    void adapter.getQuizForTechnician(artifactId).then((q) => {
      if (!cancelled) {
        setQuiz(q);
        setAnswers(q ? new Array(q.questions.length).fill(-1) : []);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, artifactId, adapter, prefetchedQuiz]);

  if (!open) return null;

  // No approved quiz → nothing to show; never blocks completion.
  if (quiz === null) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent data-testid="training-quiz-modal" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Quick knowledge check</DialogTitle>
            <DialogDescription data-testid="training-quiz-unavailable">
              No quiz is available for this training yet. Your completion is recorded.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onClose} data-testid="training-quiz-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const allAnswered = answers.length === quiz.questions.length && answers.every((a) => a >= 0);
  const canRetake = !result?.passed && attempts < QUIZ_MAX_ATTEMPTS;

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await adapter.submitAttempt({ artifactId, sessionId, answers });
      if (r) {
        setResult(r);
        setAttempts((n) => n + 1);
        onResult?.(r);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => {
    setResult(null);
    setAnswers(new Array(quiz.questions.length).fill(-1));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="training-quiz-modal" className="sm:max-w-lg">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle>Quick knowledge check</DialogTitle>
              <DialogDescription>
                Answer a few questions from this training. Passing the quiz does not
                verify watch time, but it helps OptiBay confirm you understood the material.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[50vh] overflow-y-auto">
              {quiz.questions.map((q, qi) => (
                <div key={qi} data-testid={`training-quiz-question-${qi}`}>
                  <p className="text-sm font-medium">{qi + 1}. {q.question}</p>
                  <div className="mt-1 space-y-1">
                    {q.choices.map((c, ci) => (
                      <label key={ci} className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name={`q${qi}`}
                          checked={answers[qi] === ci}
                          onChange={() =>
                            setAnswers((prev) => {
                              const next = [...prev];
                              next[qi] = ci;
                              return next;
                            })
                          }
                          data-testid={`training-quiz-q${qi}-choice-${ci}`}
                        />
                        {c}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
              <Button variant="ghost" onClick={onClose} data-testid="training-quiz-skip">
                Skip for now
              </Button>
              <Button
                onClick={submit}
                disabled={!allAnswered || submitting}
                data-testid="training-quiz-submit"
              >
                Submit answers
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle data-testid="training-quiz-result-title">
                {result.passed ? 'Quiz passed' : 'Quiz not passed yet'}
              </DialogTitle>
              <DialogDescription data-testid="training-quiz-result-subtext">
                {result.passed
                  ? 'Your completion is still user-reported, but this quiz improves confidence that you understood the training.'
                  : 'You can review the training and try again. Your shop may require a passing quiz for credit.'}
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm" data-testid="training-quiz-score">
              Score: {result.scorePct}% ({result.correctCount}/{result.totalQuestions})
            </p>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto text-xs text-muted-foreground">
              {result.questionResults.map((qr, i) => (
                <div key={i} data-testid={`training-quiz-explain-${i}`}>
                  <span className={qr.correct ? 'text-green-600' : 'text-amber-700'}>
                    {qr.correct ? '✓' : '✗'}
                  </span>{' '}
                  {qr.explanation}
                </div>
              ))}
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
              {canRetake && (
                <Button variant="outline" onClick={retake} data-testid="training-quiz-retake">
                  Retake quiz
                </Button>
              )}
              <Button onClick={onClose} data-testid="training-quiz-done">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
